import { DialogCloseEvent } from "@theme/dialog";
import { getCookie, setCookie } from "@theme/utilities";

const selectors = {
  GEOLOCATION_MODAL_SELECTOR: '[data-selector="geolocation-modal"]',
  GEOLOCATION_DESCRIPTION_SELECTOR: '[data-selector="geolocation-description"]',
  GEOLOCATION_BUTTON_REDIRECT_SELECTOR: '[data-selector="geolocation-button-redirect"]'
};

const COOKIE_NAME = "geolocationModal";

class GeolocationModal extends HTMLElement {
  constructor() {
    super();

    this.redirectButtonEl = null;
    this.modalDescriptionEl = null;
    this.abortController = null;
  }

  /** @returns {import('@theme/dialog').DialogComponent | null} */
  get #geolocationModal() {
    return this.querySelector(selectors.GEOLOCATION_MODAL_SELECTOR)
  }

  connectedCallback() {
    if (window.marketLocalizationData?.designMode || getCookie(COOKIE_NAME)) return;

    if (!this.#geolocationModal) {
      console.warn("Geolocation modal dialog component not found");
      return;
    }

    if (!window.marketLocalizationData?.countries?.length) {
      console.warn("Geolocation countries configuration is missing");
      return;
    }

    this.#initializeElements();
    this.#fetchLocation();
    this.#attachEventListeners();
  }

  #initializeElements() {
    this.abortController = new AbortController();
    this.redirectButtonEl = this.querySelector(selectors.GEOLOCATION_BUTTON_REDIRECT_SELECTOR);
    this.modalDescriptionEl = this.querySelector(selectors.GEOLOCATION_DESCRIPTION_SELECTOR);
  }

  #attachEventListeners() {
    this.#geolocationModal?.addEventListener(DialogCloseEvent.eventName, () => {
      setCookie(COOKIE_NAME, "true", 1);
    })
  }

  async #fetchLocation() {
    const { countries, international } = window.marketLocalizationData;
    const rootUrl = window.Shopify.routes.root;
    const storeCountryCode = window.Shopify.country;
    let response;

    try {
      response = await fetch(
        `${rootUrl}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${storeCountryCode}`,
      ).then((r) => r.json());
    } catch (error) {
      console.warn("Failed to fetch geolocation data", error);
      return;
    }

    if (!response?.detected_values) {
      console.warn("No response from API endpoint");
      return;
    }

    const countryUser = response.detected_values?.country?.handle;
    const matchedCountry = countries.find(
      (/** @type {{ isoCode: string; }} */ country) =>  country.isoCode === countryUser,
    );
    const selectedCountry = matchedCountry || international;

    if (countryUser === Shopify.country || !selectedCountry) return;

    const { name, redirectUrl } = selectedCountry;

    if (!name || !redirectUrl) {
      console.warn("Geolocation skipped: missing name or redirect URL");
      return;
    }

    if (this.redirectButtonEl) {
      const newTextButton = this.redirectButtonEl.textContent?.replace("{{ country }}", name);

      this.redirectButtonEl.textContent = newTextButton;
      this.redirectButtonEl.setAttribute("href", redirectUrl);
    }

    if (this.modalDescriptionEl) {
      const modalNewDescription = this.modalDescriptionEl.textContent?.replace("{{ country }}", name);

      this.modalDescriptionEl.textContent = modalNewDescription;
    }

    this.#geolocationModal?.showDialog();
  }

  disconnectedCallback() {
    if (!this.abortController) return;
    this.abortController?.abort();
    this.abortController = null;
  }
}

if (!customElements.get("geolocation-modal")) {
  customElements.define("geolocation-modal", GeolocationModal);
}
